import { readCookie, writeCookie } from './cookies.js';
import { isUuid, uuid } from './uuid.js';

/**
 * Event delivery.
 *
 * Primary path is the pixel SDK (`tdc('track', name, props)`) — the plugin does not
 * modify the SDK or the ingest service, so events ride the existing pipe. The loader
 * stub queues calls made before the SDK arrives and drains them on load.
 *
 * Fallback path exists for the one case the stub cannot survive: the SDK never loads.
 * Then the queued events would sit in memory forever, so we POST them to /v1/collect
 * ourselves, in the exact shape the service validates. Two rules there are not
 * negotiable: `cookie_id` must be a UUID and `ts` must be strict ISO-8601, otherwise
 * the row is rejected after three silent retries and lost with no dead-letter queue.
 */

export const VISITOR_COOKIE = '_td_sid';

const VISITOR_TTL_SECONDS = 365 * 24 * 60 * 60;

const UTM_PREFIXES = ['utm_', 'gad_'];
const UTM_KEYS = ['gclid', 'fbclid', 'msclkid', 'twclid'];

const REQUEST_TIMEOUT_MS = 5000;

// Browsers reject a keepalive request whose body exceeds 64 KiB — an order with a long
// items[] can get there, and the rejection would lose the event outright.
const KEEPALIVE_MAX_BYTES = 64 * 1024;

export class Transport {
  constructor({
    collectUrl,
    pixelId = null,
    win = typeof window !== 'undefined' ? window : {},
    doc = typeof document !== 'undefined' ? document : null,
    onError = null,
  }) {
    this.collectUrl = collectUrl;
    this.pixelId = pixelId;
    this.win = win;
    this.doc = doc;
    this.onError = onError;
    this.mode = 'auto';
    this.pending = [];
    // Resolved once per page — see resolveVisitor().
    this.visitor = null;
    // Which path the last send() took: 'sdk', 'sdk-queued' or 'direct'. Debug output
    // only; the caller has no decision to make on it.
    this.lastPath = null;
  }

  send(name, properties) {
    if (this.mode === 'direct') {
      return this.sendDirect(name, properties);
    }

    const tdc = this.win.tdc;

    if (typeof tdc !== 'function') {
      return this.sendDirect(name, properties);
    }

    tdc('track', name, properties);

    // The SDK has not upgraded the stub yet: keep a copy in case it never does.
    if (!tdc.callMethod) {
      this.pending.push({ name, properties });
      this.lastPath = 'sdk-queued';

      return true;
    }

    this.lastPath = 'sdk';

    return true;
  }

  /**
   * Switches to direct delivery and re-sends everything the stub swallowed.
   * Called when the SDK script errors or never upgrades the stub in time.
   */
  markSdkFailed() {
    if (this.mode === 'direct') {
      return;
    }

    this.mode = 'direct';

    const buffered = this.pending.slice();
    this.pending = [];

    // Drop the stub queue so a very late SDK load cannot re-send the same events.
    if (typeof this.win.tdc === 'function' && Array.isArray(this.win.tdc.queue)) {
      this.win.tdc.queue = this.win.tdc.queue.filter((call) => call && call[0] !== 'track');
    }

    buffered.forEach((event) => this.sendDirect(event.name, event.properties));
  }

  sdkReady() {
    return typeof this.win.tdc === 'function' && Boolean(this.win.tdc.callMethod);
  }

  /**
   * The SDK upgraded the stub and drained its own queue, so the copies kept for the
   * failure path are delivered and must not be replayed.
   */
  markSdkReady() {
    this.pending = [];
  }

  sendDirect(name, properties) {
    this.lastPath = 'direct';

    try {
      const body = JSON.stringify(this.buildPayload(name, properties));

      if (typeof this.win.fetch === 'function') {
        this.fetchDirect(body);

        return true;
      }

      if (this.win.navigator && typeof this.win.navigator.sendBeacon === 'function') {
        this.win.navigator.sendBeacon(this.collectUrl, new Blob([body], { type: 'application/json' }));

        return true;
      }
    } catch (error) {
      this.report(error);
    }

    return false;
  }

  /**
   * The request itself: bounded by an abort timeout so a hanging endpoint cannot keep
   * the connection open for the rest of the page's life, and reported on a rejection —
   * the service answers 200 long before the row is stored, so a 4xx/5xx here is the
   * only failure the browser can see at all.
   */
  fetchDirect(body) {
    const controller =
      typeof this.win.AbortController === 'function' ? new this.win.AbortController() : null;
    const timer = controller && typeof this.win.setTimeout === 'function'
      ? this.win.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
      : null;

    const request = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: body.length <= KEEPALIVE_MAX_BYTES,
      credentials: 'omit',
    };

    if (controller) {
      request.signal = controller.signal;
    }

    this.win
      .fetch(this.collectUrl, request)
      .then((response) => {
        if (response && response.ok === false) {
          this.report(new Error('collect rejected with HTTP ' + response.status));
        }
      })
      .catch((error) => this.report(error))
      .then(() => {
        if (timer !== null && typeof this.win.clearTimeout === 'function') {
          this.win.clearTimeout(timer);
        }
      });
  }

  /**
   * The exact request body POST /v1/collect validates.
   */
  buildPayload(name, properties) {
    const loc = this.win.location || { href: '', search: '' };
    const visitor = this.resolveVisitor();
    const props = Object.assign({}, properties || {});

    // Mirrors the server side: a cookie we could not use is measurable rather than
    // silently replaced.
    if (visitor.sidMissing) {
      props.sid_missing = true;
    }

    return {
      event: name,
      properties: props,
      cookie_id: visitor.id,
      ts: new Date().toISOString(),
      page: {
        url: loc.href || '',
        title: this.doc ? this.doc.title : '',
        referrer: this.doc && this.doc.referrer ? this.doc.referrer : null,
      },
      utm: this.utm(),
      identity: this.identity(),
      // `source_id` is a non-Nullable String column; send an empty string rather than
      // null so the insert cannot be rejected.
      source_id: this.pixelId || '',
    };
  }

  /**
   * The SDK's visitor cookie. When it is absent — the SDK never ran — we mint one with
   * the SDK's own attributes so the visitor is not split in two once it recovers.
   *
   * A cookie that already exists is never rewritten, whatever its shape. It is the join
   * key for order attribution in Postgres (`orders.meta_data._td_sid`) and not every
   * historical value is a UUID — see the note in src/Checkout/SessionMetaCapture.php.
   * Replacing a legacy one would silently sever that visitor's past orders, and this
   * path runs precisely when the SDK failed to load: in-app WebViews and adblockers.
   *
   * A value we cannot put in `cookie_id` (the ingest rejects a non-UUID *after*
   * returning 200) is reported as `sid_missing` instead, which is exactly what the
   * server side does in src/Dispatch/CollectClient.php.
   *
   * Resolved once and cached for the life of the page. Two of the paths below do not
   * settle on their own: a legacy non-UUID cookie mints a substitute id, and a
   * cookie-blocked browser cannot persist the one it generates. Called per event, both
   * would hand out a different UUID every time and one visitor would arrive as one
   * visitor per event — and cookie-blocked in-app WebViews are exactly the traffic this
   * fallback exists for.
   */
  resolveVisitor() {
    if (this.visitor) {
      return this.visitor;
    }

    const existing = this.doc ? readCookie(VISITOR_COOKIE, this.doc) : null;

    if (existing) {
      this.visitor = isUuid(existing)
        ? { id: existing, sidMissing: false }
        : { id: uuid(), sidMissing: true };

      return this.visitor;
    }

    const generated = uuid();

    if (this.doc) {
      try {
        writeCookie(VISITOR_COOKIE, generated, VISITOR_TTL_SECONDS, this.doc);
      } catch (e) {
        /* cookie-blocked: the event still lands, attribution just cannot persist */
      }
    }

    this.visitor = { id: generated, sidMissing: false };

    return this.visitor;
  }

  visitorId() {
    return this.resolveVisitor().id;
  }

  utm() {
    const search = (this.win.location && this.win.location.search) || '';
    const utm = {};

    search
      .replace(/^\?/, '')
      .split('&')
      .forEach((pair) => {
        if (!pair) {
          return;
        }

        const parts = pair.split('=');
        const key = decodeParam(parts[0] || '').toLowerCase();
        const value = decodeParam((parts[1] || '').replace(/\+/g, ' '));

        if (!key) {
          return;
        }

        const matchesPrefix = UTM_PREFIXES.some((prefix) => key.indexOf(prefix) === 0);

        if (matchesPrefix || UTM_KEYS.indexOf(key) !== -1) {
          utm[key] = value;
        }
      });

    return utm;
  }

  identity() {
    const identity = this.win.__trackdaddy_identity;

    if (!identity || typeof identity !== 'object') {
      return {};
    }

    const payload = {};

    if (identity.user_id) {
      payload.user_id = String(identity.user_id);
    }

    // Never send a raw e-mail from the fallback path; the SDK hashes it too.
    if (identity.email_hash) {
      payload.email_hash = String(identity.email_hash);
    }

    return payload;
  }

  report(error) {
    if (typeof this.onError === 'function') {
      this.onError(error);
    }
  }
}

/**
 * A truncated escape sequence in the query string ("?utm_term=%E0") makes
 * decodeURIComponent throw; the raw value is still worth more than a lost event.
 */
function decodeParam(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}
